import React, { useEffect, useState } from 'react';
import { TextField, FormControlLabel, RadioGroup, Radio, FormLabel, Switch, Grid, Fade, Typography, Box, FormControl, useTheme } from '@mui/material';
import { LoadingSpinnerType } from '../../stores/ConfigStore';

const MAX_CHARS = 35;

interface IDesignFormProps {
  companyLogoUrlValue: string;
  companyNameValue: string;
  showLogoValue: boolean;
  loadingSpinnerTypeValue: string;
  placeholderUrlValue: string;
  companyHexColorValue: string;
  openLinksinModalValue: boolean;
  setCompanyLogoUrlValue: (value: string) => void;
  setCompanyNameValue: (value: string) => void;
  setShowLogoValue: (value: boolean) => void;
  setLoadingSpinnerTypeValue: (value: LoadingSpinnerType) => void;
  setCompanyHexColorValue: (value: string) => void;
  setOpenLinksInModal: (value: boolean) => void;
}

const DesignForm: React.FC<IDesignFormProps> = ({
  companyLogoUrlValue,
  companyNameValue,
  showLogoValue,
  loadingSpinnerTypeValue,
  placeholderUrlValue,
  companyHexColorValue,
  openLinksinModalValue,
  setCompanyLogoUrlValue,
  setCompanyNameValue,
  setShowLogoValue,
  setLoadingSpinnerTypeValue,
  setCompanyHexColorValue,
  setOpenLinksInModal
}) => {
  const theme = useTheme();
  const [localCompanyLogoUrl, setLocalCompanyLogoUrl] = useState(companyLogoUrlValue);
  const [isValidImageUrl, setIsValidImageUrl] = useState(true);

  const [localCompanyName, setLocalCompanyName] = useState(companyNameValue);

  const isDisabled = placeholderUrlValue !== '';

  const [localHexColor, setLocalHexColor] = useState(companyHexColorValue);
  const [isValidHexColor, setIsValidHexColor] = useState(true);

  useEffect(() => {
    if (localCompanyLogoUrl) {
      const img = new Image();
      img.onload = () => setIsValidImageUrl(true);
      img.onerror = () => setIsValidImageUrl(false);
      img.src = localCompanyLogoUrl;
    } else {
      setIsValidImageUrl(true);
    }
  }, [localCompanyLogoUrl]);

  useEffect(() => {
    const isValid = localHexColor === '' || /^#([0-9A-F]{3}){1,2}$/i.test(localHexColor);
    setIsValidHexColor(isValid);
  }, [localHexColor]);


  return (
    <form autoComplete="off" autoCorrect="off" style={{ width: "100%" }}>
      <Grid container alignItems="flex-start" spacing={{ xs: 3, lg: 5 }}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl fullWidth style={{ position: 'relative' }}>
              <TextField
                name="companyColor"
                label="Firmen Farbcode"
                fullWidth
                margin="normal"
                helperText={isValidHexColor ? "Geben Sie den Hex-Farbcode Ihrer Firma ein, um das Design anzupassen (z.B. #FFFFFF)."
                  : "Ungültiger Hex-Farbcode. Bitte geben Sie einen gültigen Code im Format #RRGGBB ein."}
                value={localHexColor}
                onChange={(e) => setLocalHexColor(e.target.value)}
                onBlur={() => setCompanyHexColorValue(localHexColor)}
                error={!isValidHexColor}
                InputProps={{
                  endAdornment: (
                    <Box
                      sx={{
                        height: "54px",
                        width: "54px",
                        backgroundColor: !isValidHexColor || localHexColor.length === 0 ? theme.palette.primary.main : localHexColor,
                        borderRadius: "3px",
                        borderStartStartRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: "none",
                        position: 'absolute',
                        right: '1px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    />
                  ),
                }}
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={3} alignSelf="start">
          <FormControl margin='normal'>
            <FormLabel component="legend">
              Logo anzeigen
            </FormLabel>
            <Switch
              checked={showLogoValue}
              onChange={(e) => setShowLogoValue(e.target.checked)}
            />
          </FormControl>
        </Grid>
        {showLogoValue ? (
          <Grid item xs={9}>
            <TextField
              name="companyLogo"
              label="Firmenlogo-URL"
              fullWidth
              margin="normal"
              helperText={isValidImageUrl ? "Geben Sie die URL Ihres Firmenlogos ein." : "Die eingegebene URL ist ungültig oder enthält kein Bild. Bitte geben Sie eine gültige Bild-URL ein."}
              value={localCompanyLogoUrl}
              onChange={(e) => setLocalCompanyLogoUrl(e.target.value)}
              onBlur={() => isValidImageUrl && setCompanyLogoUrlValue(localCompanyLogoUrl)}
              error={!isValidImageUrl}
            />
          </Grid>
        ) : (
          <Grid item xs={9}>
            <TextField
              name="companyLogo"
              label="Hotel Name"
              helperText={!localCompanyName.length ? "Dieser Text wird anstelle des Logos angezeigt" : `${localCompanyName.length}/${MAX_CHARS} Zeichen verbleibend`}
              fullWidth
              margin="normal"
              value={localCompanyName}
              onChange={(e) => setLocalCompanyName(e.target.value)}
              onBlur={() => setCompanyNameValue(localCompanyName)}
              inputProps={{ maxLength: MAX_CHARS }}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset" margin='normal'>
            <FormLabel component="legend" disabled={isDisabled}>Lade-Indikator</FormLabel>
            <RadioGroup
              name="loadingSpinner"
              value={loadingSpinnerTypeValue}
              onChange={(e) => setLoadingSpinnerTypeValue(e.target.value as LoadingSpinnerType)}
              row
            >
              <FormControlLabel
                disabled={isDisabled}
                value={LoadingSpinnerType.Grid}
                control={<Radio />}
                label="Raster" />
              <FormControlLabel
                disabled={isDisabled}
                value={LoadingSpinnerType.Watch}
                control={<Radio />}
                label="Uhr" />
              <FormControlLabel
                disabled={isDisabled}
                value={LoadingSpinnerType.Rings}
                control={<Radio />}
                label="Ringe" />
              <FormControlLabel
                disabled={isDisabled}
                value={LoadingSpinnerType.Casablanca}
                control={<Radio />}
                label="Casablanca" />
            </RadioGroup>
            {isDisabled && (
              <Typography component="legend" variant="body1" display="block">
                Diese Option ist nur ohne Platzhalter-URL sichtbar.
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset" margin='normal'>
            <FormLabel component="legend">Link-Öffnungseinstellungen</FormLabel>
            <Typography component="legend" variant="body1" gutterBottom>
              Wählen Sie, wie Links in der Anwendung geöffnet werden sollen.
            </Typography>
            <RadioGroup
              value={openLinksinModalValue}
              onChange={(e) => setOpenLinksInModal(e.target.value === 'true')}
              aria-label="link-open-settings"
              name="link-open-settings"
              row>
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="In einem Modal öffnen"
                labelPlacement="end"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="In eigenem Fenster öffnen"
                labelPlacement="end"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

      </Grid>
    </form>
  );
};

export default DesignForm;
